import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Lightning, CircleNotch, Check, X, VideoCamera, FilmStrip, Sparkle,
  Play, Download, ArrowRight, CalendarBlank, Clock, Trash, CaretLeft,
  CaretRight, TiktokLogo, InstagramLogo, YoutubeLogo, LinkedinLogo, CheckCircle,
} from "@phosphor-icons/react";
import { API } from "@/lib/api";

const PLATFORM_ICONS = {
  tiktok: TiktokLogo,
  instagram: InstagramLogo,
  youtube: YoutubeLogo,
  linkedin: LinkedinLogo,
};

const PLATFORM_COLORS = {
  tiktok: "text-zinc-900",
  instagram: "text-pink-600",
  youtube: "text-red-600",
  linkedin: "text-blue-600",
};

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const ContentCalendar = ({ batches, onSwitchToCreate }) => {
  const [schedules, setSchedules] = useState([]);
  const [activeSchedule, setActiveSchedule] = useState(null);
  const [scheduling, setScheduling] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState("tiktok");
  const [loading, setLoading] = useState(true);

  const fetchSchedules = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/calendar/schedules`);
      setSchedules(res.data.schedules || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSchedules(); }, [fetchSchedules]);

  const handleAutoSchedule = async () => {
    if (!selectedBatch) return;
    setScheduling(true);
    try {
      const res = await axios.post(`${API}/calendar/auto-schedule`, {
        batch_id: selectedBatch,
        platform: selectedPlatform,
      });
      setActiveSchedule(res.data);
      fetchSchedules();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to schedule");
    } finally {
      setScheduling(false);
    }
  };

  const handleMarkPosted = async (scheduleId, videoId) => {
    try {
      await axios.patch(`${API}/calendar/item/${scheduleId}/${videoId}`, { status: "posted" });
      if (activeSchedule?.schedule_id === scheduleId) {
        setActiveSchedule(prev => ({
          ...prev,
          items: prev.items.map(item =>
            item.video_id === videoId ? { ...item, status: "posted" } : item
          ),
        }));
      }
      fetchSchedules();
    } catch (e) { console.error(e); }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      await axios.delete(`${API}/calendar/schedule/${scheduleId}`);
      if (activeSchedule?.schedule_id === scheduleId) setActiveSchedule(null);
      fetchSchedules();
    } catch (e) { console.error(e); }
  };

  const handleViewSchedule = async (scheduleId) => {
    try {
      const res = await axios.get(`${API}/calendar/schedule/${scheduleId}`);
      setActiveSchedule({ schedule_id: scheduleId, items: res.data.schedule.items, total: res.data.schedule.total_items });
    } catch (e) { console.error(e); }
  };

  // Group schedule items by day for calendar view
  const groupedByDay = {};
  if (activeSchedule?.items) {
    activeSchedule.items.forEach(item => {
      const day = item.day_label;
      if (!groupedByDay[day]) groupedByDay[day] = [];
      groupedByDay[day].push(item);
    });
  }

  const completedBatches = batches.filter(b => b.status === "completed" || b.status === "partial");

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <CircleNotch size={28} className="animate-spin text-[#E91E8C]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Schedule Creator */}
      {!activeSchedule && (
        <>
          <div className="card p-5 space-y-4">
            <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-2">
              <CalendarBlank size={16} className="text-[#E91E8C]" />
              Auto-Schedule Videos
            </h3>
            <p className="text-xs text-zinc-400">Select a completed batch and platform to auto-schedule videos across the week at optimal posting times.</p>

            {completedBatches.length === 0 ? (
              <div className="text-center py-6">
                <CalendarBlank size={32} className="text-zinc-300 mx-auto mb-2" />
                <p className="text-sm text-zinc-500">No completed batches yet</p>
                <button onClick={onSwitchToCreate} className="text-sm text-[#E91E8C] font-medium hover:underline mt-1" data-testid="calendar-go-create">
                  Create a batch first
                </button>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-zinc-500 font-medium mb-1.5 block">Batch</label>
                    <select
                      value={selectedBatch}
                      onChange={(e) => setSelectedBatch(e.target.value)}
                      className="input-field text-sm"
                      data-testid="calendar-batch-select"
                    >
                      <option value="">Select a batch...</option>
                      {completedBatches.map(b => (
                        <option key={b.id} value={b.id}>
                          {new Date(b.created_at).toLocaleDateString()} — {b.completed_count || 0}/{b.total_jobs} videos
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-zinc-500 font-medium mb-1.5 block">Platform</label>
                    <div className="flex gap-2">
                      {Object.entries(PLATFORM_ICONS).map(([key, Icon]) => (
                        <button
                          key={key}
                          onClick={() => setSelectedPlatform(key)}
                          data-testid={`calendar-platform-${key}`}
                          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                            selectedPlatform === key
                              ? "border-[#E91E8C] bg-[#E91E8C]/5 text-[#E91E8C]"
                              : "border-zinc-200 text-zinc-500 hover:border-zinc-300"
                          }`}
                        >
                          <Icon size={16} weight="fill" />
                          <span className="hidden sm:inline capitalize">{key}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleAutoSchedule}
                  disabled={!selectedBatch || scheduling}
                  className="btn-primary w-full flex items-center justify-center gap-2 py-3"
                  data-testid="calendar-auto-schedule-btn"
                >
                  {scheduling ? (
                    <><CircleNotch size={18} className="animate-spin" /> Scheduling...</>
                  ) : (
                    <><CalendarBlank size={18} weight="fill" /> Auto-Schedule</>
                  )}
                </button>
              </>
            )}
          </div>

          {/* Past Schedules */}
          {schedules.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">Past Schedules</p>
              {schedules.map(schedule => {
                const PlatformIcon = PLATFORM_ICONS[schedule.platform] || CalendarBlank;
                const postedCount = (schedule.items || []).filter(i => i.status === "posted").length;
                return (
                  <div
                    key={schedule.id}
                    className="card p-3 flex items-center justify-between hover:bg-zinc-50 transition-all"
                    data-testid={`past-schedule-${schedule.id}`}
                  >
                    <button onClick={() => handleViewSchedule(schedule.id)} className="flex items-center gap-3 flex-1 text-left">
                      <div className="w-9 h-9 rounded-lg bg-zinc-100 flex items-center justify-center">
                        <PlatformIcon size={18} className={PLATFORM_COLORS[schedule.platform]} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-zinc-800">
                          {schedule.total_items} videos • {schedule.platform}
                        </p>
                        <p className="text-xs text-zinc-400">
                          {new Date(schedule.created_at).toLocaleDateString()} • {postedCount}/{schedule.total_items} posted
                        </p>
                      </div>
                    </button>
                    <div className="flex items-center gap-2">
                      <button onClick={() => handleViewSchedule(schedule.id)} className="text-xs text-[#E91E8C] font-medium hover:underline">
                        View
                      </button>
                      <button onClick={() => handleDeleteSchedule(schedule.id)} className="p-1.5 text-zinc-400 hover:text-red-500 transition-all" data-testid={`delete-schedule-${schedule.id}`}>
                        <Trash size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Active Schedule - Calendar View */}
      {activeSchedule && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setActiveSchedule(null)}
              className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-700 transition-all"
              data-testid="calendar-back-btn"
            >
              <CaretLeft size={16} /> Back to schedules
            </button>
            <span className="text-xs text-zinc-400">{activeSchedule.total} videos scheduled</span>
          </div>

          {/* Weekly Calendar Grid */}
          <div className="grid grid-cols-7 gap-2">
            {DAY_NAMES.map(day => (
              <div key={day} className="text-center">
                <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-2">{day}</p>
                <div className="space-y-2 min-h-[120px]">
                  {(groupedByDay[day + "day"] || []).map(item => {
                    const isPosted = item.status === "posted";
                    return (
                      <div
                        key={item.video_id}
                        data-testid={`calendar-item-${item.video_id}`}
                        className={`rounded-lg p-2 text-left border transition-all ${
                          isPosted
                            ? "bg-emerald-50 border-emerald-200"
                            : "bg-white border-zinc-200 hover:border-[#E91E8C]/50 hover:shadow-sm"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-1">
                          <div className="min-w-0 flex-1">
                            {item.type === "auto_create" ? (
                              <VideoCamera size={10} className="text-[#E91E8C] mb-0.5" />
                            ) : (
                              <FilmStrip size={10} className="text-purple-500 mb-0.5" />
                            )}
                            <p className="text-[10px] font-medium text-zinc-800 truncate leading-tight">{item.title}</p>
                            <p className="text-[9px] text-zinc-400 flex items-center gap-0.5 mt-0.5">
                              <Clock size={8} /> {item.time_label}
                            </p>
                          </div>
                          {!isPosted && (
                            <button
                              onClick={() => handleMarkPosted(activeSchedule.schedule_id, item.video_id)}
                              className="p-0.5 text-zinc-300 hover:text-emerald-500 transition-all shrink-0"
                              title="Mark as posted"
                            >
                              <CheckCircle size={14} />
                            </button>
                          )}
                          {isPosted && (
                            <CheckCircle size={14} weight="fill" className="text-emerald-500 shrink-0" />
                          )}
                        </div>
                        {item.output_url && (
                          <a href={item.output_url} target="_blank" rel="noopener noreferrer" className="text-[9px] text-[#E91E8C] hover:underline mt-1 block">
                            Download
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Schedule Summary */}
          <div className="card p-4 flex items-center justify-between">
            <div className="text-sm text-zinc-600">
              <span className="font-semibold text-zinc-900">{activeSchedule.items.filter(i => i.status === "posted").length}</span>
              <span className="mx-1">/</span>
              <span>{activeSchedule.total} posted</span>
            </div>
            <div className="flex gap-2">
              {activeSchedule.items.some(i => i.output_url) && (
                <a
                  href={activeSchedule.items.find(i => i.output_url)?.output_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-zinc-500 hover:text-zinc-700 flex items-center gap-1"
                >
                  <Download size={14} /> Preview
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentCalendar;
